import { createApi } from "@reduxjs/toolkit/query/react";
import { sharedBaseQuery } from "../../api/baseQuery";
import { MonitoringDevice } from "../../types/domain";
import { managementDashboardApi } from "../management-dashboard/apiSlice";

const invalidateSiteLayout = async (
  _arg: unknown,
  { dispatch, queryFulfilled }: { dispatch: any; queryFulfilled: Promise<any> },
) => {
  try {
    await queryFulfilled;
    dispatch(managementDashboardApi.util.invalidateTags(["SiteLayout"]));
  } catch {
    // mutation failed — no need to invalidate
  }
};

export const devicesApi = createApi({
  reducerPath: "devicesApi",
  baseQuery: sharedBaseQuery.baseQueryWithReauth,
  tagTypes: ["Device"],
  endpoints: (builder) => ({
    getDevicesByProject: builder.query<MonitoringDevice[], number>({
      query: (projectId) => ({
        url: `monitoring-devices?projectId=${projectId}`,
      }),
      providesTags: ["Device"],
    }),
    getCheckpointNamesByType: builder.query<
      string[],
      { deviceType: string; projectId?: number }
    >({
      query: ({ deviceType, projectId }) => {
        const params = new URLSearchParams({ deviceType });
        if (projectId) params.append("projectId", projectId.toString());
        return {
          url: `monitoring-devices/checkpoints/by-type?${params.toString()}`,
        };
      },
      providesTags: ["Device"],
    }),
    createDevice: builder.mutation<MonitoringDevice, Partial<MonitoringDevice>>(
      {
        query: (body) => ({
          url: `monitoring-devices`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["Device"],
        onQueryStarted: invalidateSiteLayout,
      },
    ),
    getDevice: builder.query<MonitoringDevice, string>({
      query: (deviceId) => ({ url: `monitoring-devices/${deviceId}` }),
      providesTags: (_r, _e, id) => [{ type: "Device", id }],
    }),
    updateDevice: builder.mutation<
      MonitoringDevice,
      {
        deviceId: string;
        body: Partial<MonitoringDevice> & { operation_reason?: string };
      }
    >({
      query: ({ deviceId, body }) => ({
        url: `monitoring-devices/${deviceId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Device"],
      onQueryStarted: invalidateSiteLayout,
    }),
    deleteDevice: builder.mutation<
      void,
      { deviceId: string; operation_reason: string }
    >({
      query: ({ deviceId, operation_reason }) => ({
        url: `monitoring-devices/${deviceId}`,
        method: "DELETE",
        body: { operation_reason },
      }),
      invalidatesTags: ["Device"],
      onQueryStarted: invalidateSiteLayout,
    }),
    restoreDevice: builder.mutation<
      void,
      { deviceId: string; operation_reason: string }
    >({
      query: ({ deviceId, operation_reason }) => ({
        url: `monitoring-devices/${deviceId}/restore`,
        method: "POST",
        body: { operation_reason },
      }),
      invalidatesTags: ["Device"],
      onQueryStarted: invalidateSiteLayout,
    }),
    setDamageStatus: builder.mutation<
      any,
      { deviceId: string; is_damaged: boolean; operation_reason: string }
    >({
      query: ({ deviceId, ...body }) => ({
        url: `monitoring-devices/${deviceId}/damage-status`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Device"],
      onQueryStarted: invalidateSiteLayout,
    }),
  }),
});

export const {
  useGetDevicesByProjectQuery,
  useGetCheckpointNamesByTypeQuery,
  useGetDeviceQuery,
  useCreateDeviceMutation,
  useUpdateDeviceMutation,
  useDeleteDeviceMutation,
  useRestoreDeviceMutation,
  useSetDamageStatusMutation,
} = devicesApi;
